import { GraphResolverBase } from './GraphResolverBase';
import { ComponentFactory, ViewContainerRef } from '@angular/core';
import { ConfigDataService } from '../services/config-data.service';
import { GraphDataService } from '../services/graph-data-service.service';
import { ViewPeriodService } from '../services/view-period.service';
import { BehaviorSubject, Subscription, combineLatest, filter, map, tap } from 'rxjs';
import { PopoverController } from '@ionic/angular';
import { GenericBarChartData } from '../objects/chart';
import { PIE_CHART_COLOR_LIST } from '../configs/color';
import { DynamicBarChartWrapperComponent } from '../shared-components/dynamic/dynamic-bar-chart-wrapper/dynamic-bar-chart-wrapper.component';
import { DynamicGraphAdditionalInput } from '../objects/config';
import { getAdditionalInput } from '../helpers/DynamicGraphHelper';
import { accessDepthData } from '../helpers/util';
import { Model3DLevel } from '../objects/baseGraphData';
import { shouldShowGraph, safeGet } from '../helpers/graphResolverHelper';

export class GateTrafficResolver extends GraphResolverBase {
    public async createComponent(
        componentFactory: ComponentFactory<unknown>,
        additionalInput: string | DynamicGraphAdditionalInput | undefined,
        configDataService: ConfigDataService,
        graphDataService: GraphDataService,
        _popoverController: PopoverController,
        viewPeriodService: ViewPeriodService,
        viewContainerRef: ViewContainerRef,
        subscription: Subscription
    ) {
        // load data here
        graphDataService.baseGraphData.addDependency(this.dataDependency);
        // await configDataService.loadAppConfig(); 
        const buildingName = (getAdditionalInput(additionalInput, 'building') || configDataService.isFeatureEnabled('traffic_page', 'model_user_data') ? (graphDataService.baseGraphData.selectedInteractable$.value || {}).object.userData.building : configDataService.MAIN_BUILDING) as string;
        const initialFloorName = graphDataService.selectedFloorName$.value || graphDataService.baseGraphData.selectedDirectory$.value.floor || 'ALL';
        const initialLevel = graphDataService.baseGraphData.selectedLevel$.value;
        const initialGateName = (graphDataService.baseGraphData.selectedInteractable$.value || {}).name;
        const initialZoneName = graphDataService.baseGraphData.selectedDirectory$.value.zone;
        const contribute_level = getAdditionalInput(additionalInput, 'contributeLevel') || configDataService.isFeatureEnabled('heatmap_v2', 'contribute_level') as string;
        const excludedDirectory = (getAdditionalInput(additionalInput, 'excludedDirectory')) as Model3DLevel;

        const componentRef = viewContainerRef.createComponent(componentFactory);
        const comInstance = componentRef.instance as DynamicBarChartWrapperComponent;

        const getTrafficData = (
            data: { [buildingName: string]: number | { [floorName: string]: number } | { [floorName: string]: { [gateName: string]: number } } },
            building?: string,
            floorName?: string,
            gateName?: string,
        ) => accessDepthData<number>(data, building, floorName, gateName, 0);

        const getZonePinTrafficData = (
            data: { [buildingName: string]: number | { [floorName: string]: number } | { [floorName: string]: { [gateName: string]: number } } } | { [buildingName: string]: { [floorName: string]: { [zoneName: string]: { [gateName: string]: number } } } },
            building?: string,
            floorName?: string,
            zoneName?: string,
            gateName?: string,
        ) => {
            if (!data || !data[building]) { return 0; }
            if (floorName) {
                if (zoneName && data[building][floorName]) {
                    if (gateName && data[building][floorName][zoneName]) {
                        return (data[building][floorName][zoneName][gateName] || 0) as number;
                    }
                    return (data[building][floorName][zoneName] || 0) as number;
                }
                return (data[building][floorName] || 0) as number;
            }
            return (data[building] || 0) as number;
        };
        let initialEntranceData: number;
        let initialExitData: number;
        if (contribute_level === 'building') {
            initialEntranceData = initialLevel === 'FLOOR'
                ? getTrafficData({ ...graphDataService.entranceBuildingPin$.value, ...graphDataService.entranceFloorPin$.value }, buildingName, initialFloorName, initialGateName)
                : getZonePinTrafficData(graphDataService.entranceZonePin$.value, initialFloorName, initialZoneName, initialGateName);
            initialExitData = initialLevel === 'FLOOR'
                ? getTrafficData({ ...graphDataService.exitBuildingPin$.value, ...graphDataService.exitFloorPin$.value }, buildingName, initialFloorName, initialGateName)
                : getZonePinTrafficData(graphDataService.exitZonePin$.value, initialFloorName, initialZoneName, initialGateName);
        } else {
            initialEntranceData = initialLevel === 'FLOOR' ? getTrafficData({ ...graphDataService.entranceBuildingPin$.value, ...graphDataService.entranceFloorPin$.value }, initialFloorName, initialGateName)
                : getZonePinTrafficData(graphDataService.entranceZonePin$.value, initialFloorName, initialZoneName, initialGateName);
            initialExitData = initialLevel === 'FLOOR' ? getTrafficData({ ...graphDataService.exitBuildingPin$.value, ...graphDataService.exitFloorPin$.value }, initialFloorName, initialGateName)
                : getZonePinTrafficData(graphDataService.exitZonePin$.value, initialFloorName, initialZoneName, initialGateName);
        }
        comInstance.chartLabel = [configDataService.DISPLAY_LANGUAGE.ENTRANCE, configDataService.DISPLAY_LANGUAGE.EXIT];
        comInstance.isShow = (graphDataService.baseGraphData.selectedInteractable$.value || {}).type === 'gate';
        const barData$ = new BehaviorSubject<GenericBarChartData[]>([{
            data: [initialEntranceData, null],
            color: PIE_CHART_COLOR_LIST[0],
            label: configDataService.DISPLAY_LANGUAGE.ENTRANCE,
        }, {
            data: [null, initialExitData],
            color: PIE_CHART_COLOR_LIST[1],
            label: configDataService.DISPLAY_LANGUAGE.EXIT,
        }]);
        const pinsData$ = combineLatest([
            graphDataService.entranceBuildingPin$, graphDataService.exitBuildingPin$,
            graphDataService.entranceFloorPin$, graphDataService.exitFloorPin$,
            graphDataService.entranceZonePin$, graphDataService.exitZonePin$,
            graphDataService.spEntranceBuildingFloorPin$, graphDataService.spExitBuildingFloorPin$,
            graphDataService.storeAreaEntranceExitByPinData$
        ]);

        const allSubscription$ = combineLatest([
            graphDataService.baseGraphData.selectedDirectory$,
            graphDataService.baseGraphData.selectedLevel$,
            graphDataService.baseGraphData.selectedInteractable$,
            pinsData$
        ]);

        allSubscription$.pipe(
            tap(([dir, level, gate, _pins]) => {
                comInstance.isShow = shouldShowGraph(dir, gate, level, excludedDirectory);
            }),
            filter(([dir, _level, gate]) =>
                !!dir
                && gate?.type === 'gate'
            ),
            map(([dir, level, gate, pins]) => {
                let buildingName = configDataService.isFeatureEnabled('traffic_page', 'model_user_data') ? gate.object.userData.building : dir.building;
                let floorName = configDataService.isFeatureEnabled('traffic_page', 'model_user_data') ? gate.object.userData.floor : dir.floor;
                let zoneName = configDataService.isFeatureEnabled('traffic_page', 'model_user_data') ? dir.zone === null ? (gate.object.userData.zone as string).split(',')[0] : ((gate.object.userData.zone as string).split(',')).find(str => str.includes(dir.zone)) : dir.zone;
                if (zoneName === 'pavilion_000_g_001_2nd_reception') {
                    floorName = 'pavilion_000_g_001_2nd';
                }
                if (configDataService.isFeatureEnabled('multiple_organization') && floorName === 'onesiam_plus_007_5f') {
                    const floorIndexMainBuilding = Object.keys(configDataService.FLOOR_OBJECTS[configDataService.MAIN_BUILDING]).findIndex(k => k === floorName);
                    floorName = Object.keys(configDataService.FLOOR_OBJECTS[buildingName])[floorIndexMainBuilding];
                }
                if (configDataService.isFeatureEnabled('heatmap_v2', 'depth_zone')) {
                    buildingName = configDataService.isFeatureEnabled('heatmap_v2', 'depth_zone');
                    floorName = zoneName;
                }
                return {
                    pinsBuildingEntrance: floorName === 'siam_paragon_5th' ? { ...pins[6] } : { ...pins[0] },
                    pinsBuildingExit: floorName === 'siam_paragon_5th' ? { ...pins[7] } : { ...pins[1] },
                    pinsFloorEntrance: { ...pins[2] },
                    pinsFloorExit: { ...pins[3] },
                    pinsZoneEntrance: { ...pins[4] },
                    pinsZoneExit: { ...pins[5] },
                    pinsStoreEntranceExit: { ...pins[8] },
                    buildingName: buildingName,
                    floorName: floorName,
                    zoneName: zoneName,
                    interactableName: gate.name,
                    nextLevel: level,
                };
            }),
            map(inputs => {
                const buildingName = inputs.buildingName;
                const floorName = inputs.floorName;
                const zoneName = inputs.zoneName;
                let entranceData = 0;
                let exitData = 0;

                if (inputs.nextLevel === 'FLOOR') {
                    // try building → floor
                    entranceData = safeGet<number>(
                        inputs.pinsBuildingEntrance,
                        [buildingName, floorName, inputs.interactableName],
                        0
                    );
                    exitData = safeGet<number>(
                        inputs.pinsBuildingExit,
                        [buildingName, floorName, inputs.interactableName],
                        0
                    );

                    // if that was 0, try floor → floor
                    if (entranceData === 0 && exitData === 0) {
                        entranceData = safeGet<number>(
                            inputs.pinsFloorEntrance,
                            [buildingName, floorName, inputs.interactableName],
                            0
                        );
                        exitData = safeGet<number>(
                            inputs.pinsFloorExit,
                            [buildingName, floorName, inputs.interactableName],
                            0
                        );
                    }

                    // if *still* 0, and zoneName is set, try zone → floor → zone else try pin of store
                    if (entranceData === 0 && exitData === 0) {
                        if (zoneName) {
                            entranceData = safeGet<number>(
                                inputs.pinsZoneEntrance,
                                [buildingName, floorName, zoneName, inputs.interactableName],
                                0
                            );
                            exitData = safeGet<number>(
                                inputs.pinsZoneExit,
                                [buildingName, floorName, zoneName, inputs.interactableName],
                                0
                            );
                        } else {
                            const entranceDatas = safeGet<number[]>(
                                inputs.pinsStoreEntranceExit,
                                [inputs.interactableName, "entrance"],
                                []
                            );
                            const exitDatas = safeGet<number[]>(
                                inputs.pinsStoreEntranceExit,
                                [inputs.interactableName, "exit"],
                                []
                            );
                            if (entranceDatas.length > 0) {
                                entranceData = entranceDatas[entranceDatas.length - (viewPeriodService.isLiveMode ? 1 : 2)];
                            }
                            if (exitDatas.length > 0) {
                                exitData = exitDatas[exitDatas.length - (viewPeriodService.isLiveMode ? 1 : 2)];
                            }
                        }
                    }
                }
                else if (inputs.nextLevel === 'ZONE') {
                    if (configDataService
                        .isFeatureEnabled('graph_data', 'gate_traffic')
                        ?.zone_as_building
                    ) {
                        entranceData = safeGet<number>(
                            inputs.pinsBuildingEntrance,
                            [buildingName, floorName, inputs.interactableName],
                            0
                        );
                        exitData = safeGet<number>(
                            inputs.pinsBuildingExit,
                            [buildingName, floorName, inputs.interactableName],
                            0
                        );
                    } else {
                        entranceData = safeGet<number>(
                            inputs.pinsZoneEntrance,
                            [buildingName, floorName, zoneName, inputs.interactableName],
                            0
                        );
                        exitData = safeGet<number>(
                            inputs.pinsZoneExit,
                            [buildingName, floorName, zoneName, inputs.interactableName],
                            0
                        );
                    }
                }
                return [
                    {
                        data: [entranceData, null],
                        color: PIE_CHART_COLOR_LIST[0],
                        label: configDataService.DISPLAY_LANGUAGE.ENTRANCE,
                    },
                    {
                        data: [null, exitData],
                        color: PIE_CHART_COLOR_LIST[1],
                        label: configDataService.DISPLAY_LANGUAGE.EXIT,
                    }
                ];
            })
        ).subscribe(barData$);

        comInstance.title = configDataService.DISPLAY_LANGUAGE.TRAFFIC;
        comInstance.isLock = this.isLock;
        comInstance.isShowLegend = false;
        comInstance.data$ = barData$;
        comInstance.isStacked = 'XY';
        comInstance.xAxesRotation = 90;
        return comInstance;
    }

}
